// @flow
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { noop, flowRight } from 'lodash/fp';
import { alerts, feedback } from '~/core/constants';
import handleApi from '~/core/state/handle-api';
import { patchProduct } from '~/core/api/products/products.api';
import { requestAlert } from '~/core/state/alerts/actions';
import { bulbModes } from '~/core/constants/devices';
import { changePicker } from '~/core/state/ui/bulbs';

import { getTypeForProduct, getProductItemById } from '../selectors';
import { undoProductChange } from '../actions';
import { bulbSaved, toggleDone } from './actions';
import { CHANGE_BRIGHTNESS, CHANGE_COLOR_MODE, COLOR_CHANGED, TOGGLE_LIGHTBULB, TALK_TO_LIGHT } from './constants';
import { getColorModeForBulb, getHSVForBulb, getTemperatureForBulb, getCanChangeToWhite } from './selectors';

import type { ChangeBulbBrightnessAction, ChangeBulbColourAction, ChangeBulbStatusAction } from './types';
import type { Effect } from 'redux-saga';

export function* changeBulbBrightnessSaga({ payload }: ChangeBulbBrightnessAction): Generator<Effect | noop, *, *> {
    const { productId, brightness, isOn } = payload;
    const props = { id: productId };
    const productType = yield select(getTypeForProduct, props);
    const colourMode = yield select(getColorModeForBulb, props);
    const [hue, saturation] = yield select(getHSVForBulb, props);

    const patch =
        colourMode === bulbModes.colour
            ? {
                  hue,
                  saturation,
                  value: brightness
              }
            : {
                  brightness
              };

    const fullPatch = isOn
        ? patch
        : {
              ...patch,
              status: 'ON'
          };

    const [, error] = yield call(handleApi(patchProduct), fullPatch, productType, productId);

    if (error) {
        yield put(undoProductChange(CHANGE_BRIGHTNESS));
        yield put(requestAlert(feedback.generic.UNEXPECTED_ERROR, alerts.types.ERROR));
    }

    yield put(bulbSaved(payload));
}

function* watchChangeBulbBrightnessSaga(): Generator<Effect, *, *> {
    yield takeLatest([CHANGE_BRIGHTNESS], changeBulbBrightnessSaga);
}

export function* changeBulbStatusSaga({
    payload: { productId }
}: ChangeBulbStatusAction): Generator<Effect | noop, *, *> {
    const props = { id: productId };
    const nextValue = (yield select(getProductItemById, props)).getIn(['state', 'status']);
    const productType = yield select(getTypeForProduct, props);

    const [, error] = yield call(handleApi(patchProduct), { status: nextValue }, productType, productId);

    if (error) {
        yield put(undoProductChange(TOGGLE_LIGHTBULB));
        yield put(requestAlert(feedback.generic.UNEXPECTED_ERROR, alerts.types.ERROR));
    }

    yield put(toggleDone(error || { productId }));
}

function* watchChangeBulbStatusSaga(): Generator<Effect, *, *> {
    yield takeLatest(TOGGLE_LIGHTBULB, changeBulbStatusSaga);
}

const maxSaturationHoneycombWillAccept = 99;
const minSaturation = 0;

const max = maxValue => value => Math.max(value, maxValue);
const min = minValue => value => Math.min(value, minValue);
const normaliseSaturation = flowRight(max(minSaturation), min(maxSaturationHoneycombWillAccept));

export function* getPatch(
    props: { id: string },
    productType: string,
    mode: string,
    isCustomRange: boolean
): Generator<any | noop, *, *> {
    const [hue, saturation, value] = yield select(getHSVForBulb, props);
    const colourTemperature = yield select(getTemperatureForBulb, props);
    const canChangeToWhite = yield select(getCanChangeToWhite, props);
    const colourMode = mode || (isCustomRange ? bulbModes.white : bulbModes.colour);

    if (isCustomRange) {
        return {
            colourTemperature
        };
    }

    if (mode === bulbModes.white && !canChangeToWhite) {
        return {
            colourMode,
            colourTemperature
        };
    }

    return {
        colourMode,
        hue,
        saturation: normaliseSaturation(saturation),
        value
    };
}

export function* changeBulbColourSaga({
    payload: { productId, isCustomRange, mode, pickerType }
}: ChangeBulbColourAction): Generator<Effect | noop, *, *> {
    const props = { id: productId };
    const productType = yield select(getTypeForProduct, props);
    const patch = yield call(getPatch, props, productType, mode, isCustomRange);

    const [, error] = yield call(handleApi(patchProduct), patch, productType, productId);

    if (error) {
        yield put(undoProductChange(COLOR_CHANGED));
        yield put(requestAlert(feedback.generic.UNEXPECTED_ERROR, alerts.types.ERROR));
    }

    if (pickerType) {
        yield put(
            changePicker({
                pickerType,
                productId
            })
        );
    }

    yield put(bulbSaved(error || { productId }));
}

function* watchChangeBulbColorSaga(): Generator<Effect, *, *> {
    yield takeEvery([COLOR_CHANGED, CHANGE_COLOR_MODE], changeBulbColourSaga);
}

function* talkToLightSaga({ payload }): Generator<Effect, *, *> {
    const { id, type, patch } = payload;
    const productType = yield select(getTypeForProduct, payload);
    yield call(handleApi(patchProduct), patch, type || productType, id);
}

function* watchTalkToLightSaga(): Generator<Effect, *, *> {
    yield takeEvery(TALK_TO_LIGHT, talkToLightSaga);
}

export default [
    watchTalkToLightSaga,
    watchChangeBulbBrightnessSaga,
    watchChangeBulbStatusSaga,
    watchChangeBulbColorSaga
];



// WEBPACK FOOTER //
// ./src/core/state/products/bulbs/sagas.js